import * as braze from "@braze/web-sdk";
import type { KadeweBrazeEvent, KadeweUser } from "~/types";

export default defineNuxtPlugin(() => {
    const {
        public: {
            braze: { api_key: apiKey, base_url: baseUrl, logging: enableLogging },
        },
    } = useRuntimeConfig();

    const user = useSanctumUser<KadeweUser>();
    const { refreshIdentity } = useSanctumAuth();
    const contentCards = reactive([]) as KadeweBrazeEvent[];
    let initialized = false;
    // Set once Braze was initialized, to start it again after a logout destroyed it
    let enabled = false;
    const pendingCalls: Array<() => void> = [];

    const runOrQueue = (fn: () => void) => {
        if (initialized) {
            fn();
        } else {
            pendingCalls.push(fn);
        }
    };

    let contentCardsSubscription: string | undefined;
    let ignoreCacheReplay = false;

    const onContentCardsEvent = (event: braze.ContentCardsEvent) => {
        switch (event.type) {
            case braze.ChannelEventType.ERROR:
                console.debug(`Braze LP: Content cards error: ${event.reason} (${event.retryState})`);
                return;
            case braze.ChannelEventType.CACHE_REPLAY:
                // changeUser() keeps the previous cache, so the replay may still hold the previous user's cards.
                if (ignoreCacheReplay) {
                    return;
                }
                break;
            case braze.ChannelEventType.CACHE_LOAD:
                break;
            case braze.ChannelEventType.DATA_UPDATED:
                if (!user.value?.contact_id) {
                    return;
                }
                ignoreCacheReplay = false;
                break;
            default:
                return;
        }

        const newContentCards = event.cacheSnapshot.contentCards;
        console.debug(
            `Braze LP: Content cards updated (${event.type}): ${newContentCards.cards.length} cards, ${newContentCards.getUnviewedCardCount()} unviewed, last updated at ${newContentCards.lastUpdated}`,
        );
        contentCards.splice(0, contentCards.length, ...(newContentCards.cards as KadeweBrazeEvent[]));
    };

    // Order per Braze docs: changeUser and subscribeToContentCardsEvents before openSession,
    // otherwise content cards are not refreshed on session start.
    const startSession = (contactId: string) => {
        console.debug(`Braze LP: Starting session for contact ID ${contactId}`);
        ignoreCacheReplay = braze.getUser()?.getUserId() !== contactId;
        braze.changeUser(contactId, user.value?.braze_sdk_auth ?? undefined);
        contentCardsSubscription ??= braze.subscribeToContentCardsEvents(onContentCardsEvent);
        braze.openSession();
    };

    // A second openSession() would only resume the open session, and content cards sync on session start only.
    const switchUser = (contactId: string) => {
        console.debug(`Braze LP: Switching user to contact ID ${contactId}`);
        // Don't show the previous user's cards until the refresh arrives (or forever, if it fails).
        contentCards.splice(0, contentCards.length);
        ignoreCacheReplay = true;
        braze.changeUser(contactId, user.value?.braze_sdk_auth ?? undefined);
        braze.requestContentCardsRefresh();
    };

    const endSession = () => {
        console.debug("Braze LP: Ending session");
        contentCards.splice(0, contentCards.length);
        if (contentCardsSubscription) {
            braze.removeSubscription(contentCardsSubscription);
            contentCardsSubscription = undefined;
        }
        braze.wipeData();
        // wipeData() also cancels the SDK's pending requests, so start from scratch on the next login
        braze.destroy();
        initialized = false;
    };

    const initialize = () => {
        if (initialized) {
            return;
        }

        if (!apiKey) {
            console.warn("Braze API key is not set");
            return;
        }

        braze.initialize(apiKey, {
            baseUrl: baseUrl,
            enableLogging: enableLogging,
            openCardsInNewTab: true,
            enableSdkAuthentication: true,
            localization: user.value?.language,
        });
        initialized = true;
        enabled = true;

        braze.subscribeToSdkAuthenticationFailures(async (error) => {
            console.warn("Braze LP: SDK authentication failed, refreshing signature", error);
            const previousSignature = user.value?.braze_sdk_auth;
            await refreshIdentity();

            // Re-setting the same rejected token would just trigger the next failure (and refresh) again.
            const signature = user.value?.braze_sdk_auth;
            if (signature && signature !== previousSignature) {
                braze.setSdkAuthenticationSignature(signature);
            }
        });

        if (user.value?.contact_id) {
            startSession(user.value.contact_id);
        }

        pendingCalls.splice(0).forEach((fn) => fn());
    };

    watch(
        () => user.value?.contact_id,
        (newContactId, oldContactId) => {
            if (newContactId === oldContactId) {
                return;
            }
            if (!initialized) {
                if (enabled && newContactId) {
                    initialize();
                }
                return;
            }
            if (!newContactId) {
                endSession();
            } else if (contentCardsSubscription) {
                switchUser(newContactId);
            } else {
                startSession(newContactId);
            }
        },
    );

    return {
        provide: {
            braze: {
                initialize,
                logContentCardImpression(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card impression", event);
                    braze.logContentCardImpressions([event]);
                },
                logContentCardClick(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card click", event);
                    braze.logContentCardClick(event);
                },
                requestContentCardsRefresh: () => {
                    return new Promise<void>((resolve, reject) => {
                        runOrQueue(() => {
                            if (!user.value?.contact_id) {
                                resolve();
                                return;
                            }
                            braze.requestContentCardsRefresh(
                                () => {
                                    console.debug("Content cards successfully refreshed.");
                                    resolve();
                                },
                                () => {
                                    // Since SDK v7 this fires right away while the server config is still
                                    // pending (first init); cards then arrive via the session-start refresh.
                                    console.warn("Braze LP: Content cards refresh failed or channel not enabled yet.");
                                    reject();
                                },
                            );
                        });
                    });
                },
                contentCards,
            },
        },
    };
});
